/**
 * User EEOC Training Page
 * Self-service EEOC compliance training for individual users
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Shield,
  BookOpen,
  Play,
  Award,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Clock,
  Target,
  FileText,
  ChevronRight,
  Loader2,
  Trophy,
  GraduationCap,
  ListChecks,
  Lightbulb,
  HelpCircle,
  Users
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Status Badge Component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    qualified: { icon: CheckCircle, color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', label: 'Qualified' },
    needs_improvement: { icon: AlertTriangle, color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', label: 'Needs Improvement' },
    not_qualified: { icon: XCircle, color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', label: 'Not Qualified' },
    not_started: { icon: Clock, color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400', label: 'Not Started' },
    in_progress: { icon: Play, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', label: 'In Progress' },
    completed: { icon: CheckCircle, color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', label: 'Completed' },
  };
  
  const config = statusConfig[status] || statusConfig.not_started;
  const Icon = config.icon;
  
  return (
    <Badge className={cn("gap-1", config.color)}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

// EEOC Reference Library Content - Comprehensive EEOC Compliance Guide
const LIBRARY_SECTIONS = [
  {
    id: 'overview',
    title: 'EEOC Overview',
    icon: Shield,
    description: 'Understanding Equal Employment Opportunity laws',
    content: [
      'The EEOC enforces federal laws prohibiting employment discrimination',
      'Protected characteristics include race, color, religion, sex, national origin, age (40+), disability, and genetic information',
      'Discrimination is prohibited in hiring, firing, pay, promotions, training, and all employment decisions',
      'Violations can result in lawsuits, fines up to $300,000 per violation, and reputational damage',
      'Both intentional discrimination and policies with discriminatory effects are illegal'
    ]
  },
  {
    id: 'protected',
    title: 'Protected Categories',
    icon: Users,
    description: '8 federally protected characteristics',
    content: [
      'Age (ADEA) - Protects individuals 40 years or older from age-based discrimination',
      'Race & Color (Title VII) - Prohibits discrimination based on race, skin color, or ethnicity',
      'National Origin - Protects against discrimination based on birthplace, ancestry, culture, or accent',
      'Religion - Covers beliefs, practices, and requires reasonable accommodation',
      'Sex/Gender - Includes pregnancy, sexual orientation, and gender identity (Bostock v. Clayton)',
      'Disability (ADA) - Protects qualified individuals; requires reasonable accommodations',
      'Genetic Information (GINA) - Prohibits using family medical history in employment decisions',
      'Pregnancy (PDA) - Cannot discriminate based on pregnancy, childbirth, or related conditions'
    ]
  },
  {
    id: 'interview',
    title: 'Interview Do\'s & Don\'ts',
    icon: ListChecks,
    description: 'What you can and cannot ask',
    content: [
      'DO: Ask about ability to perform specific job duties with or without accommodation',
      'DO: Ask about availability for the work schedule required',
      'DO: Ask about legal authorization to work in the US',
      'DO: Ask behavioral questions about past work performance',
      'DON\'T: Ask about age, birth date, or when they graduated',
      'DON\'T: Ask about marital status, children, or pregnancy plans',
      'DON\'T: Ask about religious practices, holidays observed, or church attendance',
      'DON\'T: Ask about disabilities, medical history, or workers\' compensation claims',
      'DON\'T: Ask where they were born or about citizenship (only work authorization)'
    ]
  },
  {
    id: 'scenarios',
    title: 'Common Scenarios',
    icon: Lightbulb,
    description: 'How to handle tricky situations professionally',
    content: [
      'Candidate mentions pregnancy → "Thank you for sharing. Let me focus on the job requirements..."',
      'Candidate reveals disability → "Our focus is on whether you can perform essential functions..."',
      'Candidate mentions religious practices → "We discuss accommodations after an offer is made..."',
      'Candidate shares age/graduation year → "Let\'s discuss your recent relevant experience..."',
      'Candidate has accent → Never comment on accents; focus only on communication skills needed for role',
      'Candidate mentions caregiving → Don\'t ask follow-ups; stick to schedule availability questions'
    ]
  },
  {
    id: 'redirection',
    title: 'Redirection Techniques',
    icon: Target,
    description: 'Professional ways to stay compliant',
    content: [
      '"That\'s interesting. Let me ask about your experience with [specific job requirement]..."',
      '"I appreciate you sharing. Regarding this role, can you tell me about..."',
      '"We can discuss any accommodations after an offer. For now, let\'s focus on..."',
      '"Let me redirect to your qualifications. What experience do you have with..."',
      'Acknowledge briefly → Pause → Pivot to job-relevant topic',
      'Never follow up on protected information; always redirect immediately'
    ]
  },
  {
    id: 'consequences',
    title: 'Legal Consequences',
    icon: AlertTriangle,
    description: 'Why compliance matters',
    content: [
      'Individual liability: Interviewers can be personally named in lawsuits',
      'Company fines: Up to $300,000 per violation for large employers',
      'Back pay and front pay damages for affected candidates',
      'Punitive damages for intentional discrimination',
      'Attorney fees and court costs',
      'Mandatory policy changes and compliance monitoring',
      'Reputational damage affecting recruiting and business relationships'
    ]
  }
];

export default function UserEEOC() {
  const { userUrl } = useParams();
  const navigate = useNavigate();
  const { user: authUser, tenant } = useAuth();
  const context = useOutletContext() || {};
  
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [myTests, setMyTests] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [startingTest, setStartingTest] = useState(false);

  useEffect(() => {
    fetchData();
  }, [userUrl, authUser]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let userData;
      
      // If logged in user accessing from dashboard (no userUrl), use auth context
      if (!userUrl && authUser) {
        userData = {
          id: authUser.id,
          name: authUser.name,
          email: authUser.email,
          tenant_id: tenant?.id || authUser.tenant_id
        };
        setUser(userData);
      } else if (userUrl) {
        // For passwordless users with userUrl
        const userRes = await api.get(`/users/by-url/${userUrl}`);
        userData = userRes.data;
        setUser(userData);
      } else {
        // No user data available
        setLoading(false);
        return;
      }
      
      // Get user's EEOC tests - use the user's tenant_id
      if (userData?.id) {
        try {
          // Get tenant_id from user data or use default
          const tenantId = userData.tenant_id || 'default';
          const testsRes = await api.get(`/eeoc/my-tests?user_id=${userData.id}&tenant_id=${tenantId}`);
          setMyTests(testsRes.data.tests || []);
        } catch (e) {
          console.log('No EEOC tests found or feature not enabled');
          setMyTests([]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      // Don't show error toast - the EEOC feature may just not be enabled
    } finally {
      setLoading(false);
    }
  };

  const handleStartMockTest = () => {
    // For self-service, we create a test on the fly if user doesn't have one
    // For logged-in users (no userUrl), navigate to dashboard route
    if (!userUrl) {
      navigate('/dashboard/eeoc/mock');
    } else {
      navigate(`/user/${userUrl}/eeoc/mock`);
    }
  };

  const handleStartAssessment = () => {
    // For logged-in users (no userUrl), navigate to dashboard route
    if (!userUrl) {
      navigate('/dashboard/eeoc/assessment');
    } else {
      navigate(`/user/${userUrl}/eeoc/assessment`);
    }
  };

  const handleViewResults = (test) => {
    if (test.link_id) {
      navigate(`/eeoc/test/${test.link_id}/results`);
    }
  };

  // Calculate user's current status
  const latestCompletedTest = myTests.find(t => t.test_status === 'completed');
  const hasCompletedAssessment = !!latestCompletedTest;
  const currentScore = latestCompletedTest?.score;
  const qualificationStatus = latestCompletedTest?.qualification_status;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-eeoc-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg p-6 text-white">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/20 rounded-lg">
            <Shield className="h-8 w-8" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">EEOC Compliance Training</h1>
            <p className="text-purple-100 mt-1">
              Learn and practice EEOC-compliant interviewing techniques
            </p>
          </div>
          {hasCompletedAssessment && (
            <div className="text-right">
              <div className="text-sm text-purple-200">Your Score</div>
              <div className="text-3xl font-bold">{currentScore}%</div>
              <StatusBadge status={qualificationStatus} />
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Scenario Reference Book - Study First */}
        <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-indigo-200" onClick={() => navigate(!userUrl ? '/dashboard/eeoc/scenarios' : `/user/${userUrl}/eeoc/scenarios`)}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                <BookOpen className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Scenario Book</h3>
                <p className="text-sm text-muted-foreground">Study before testing</p>
              </div>
            </div>
            <Badge variant="outline" className="w-full justify-center mt-3 text-indigo-600 border-indigo-300">
              Recommended First Step
            </Badge>
            <Button className="w-full mt-3 bg-indigo-600 hover:bg-indigo-700 text-white">
              Study Scenarios
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>

        {/* Mock Test */}
        <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-blue-200" onClick={handleStartMockTest}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Play className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Mock Test</h3>
                <p className="text-sm text-muted-foreground">Practice without scoring</p>
              </div>
            </div>
            <Badge variant="outline" className="w-full justify-center mt-3 text-blue-600 border-blue-300">
              2 Practice Scenarios
            </Badge>
            <Button className="w-full mt-3" variant="outline">
              Start Practice
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>

        {/* Assessment */}
        <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-green-200" onClick={handleStartAssessment}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Award className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Assessment</h3>
                <p className="text-sm text-muted-foreground">Official evaluation</p>
              </div>
            </div>
            <Badge variant="outline" className="w-full justify-center mt-3 text-green-600 border-green-300">
              5 Scored Scenarios
            </Badge>
            <Button className="w-full mt-3 bg-green-600 hover:bg-green-700">
              Take Assessment
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>

        {/* Quick Reference */}
        <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-amber-200" onClick={() => setActiveTab('library')}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <FileText className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Quick Reference</h3>
                <p className="text-sm text-muted-foreground">Guidelines & tips</p>
              </div>
            </div>
            <Badge variant="outline" className="w-full justify-center mt-3 text-amber-600 border-amber-300">
              6 Topics Covered
            </Badge>
            <Button className="w-full mt-3" variant="outline">
              Browse Library
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview" className="gap-2">
            <Target className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <Trophy className="h-4 w-4" />
            My Results
          </TabsTrigger>
          <TabsTrigger value="library" className="gap-2">
            <BookOpen className="h-4 w-4" />
            Library
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5" />
                About EEOC Training
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                This training helps you conduct interviews that comply with Equal Employment Opportunity Commission (EEOC) guidelines. 
                You&apos;ll practice handling scenarios where candidates may disclose protected information.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <h4 className="font-semibold text-blue-700 dark:text-blue-400 mb-2">Mock Test</h4>
                  <ul className="text-sm text-blue-600 dark:text-blue-300 space-y-1">
                    <li>• 2 practice scenarios</li>
                    <li>• No score recorded</li>
                    <li>• Unlimited attempts</li>
                    <li>• Learn from mistakes</li>
                  </ul>
                </div>
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <h4 className="font-semibold text-green-700 dark:text-green-400 mb-2">Assessment</h4>
                  <ul className="text-sm text-green-600 dark:text-green-300 space-y-1">
                    <li>• 5 evaluation scenarios</li>
                    <li>• Score recorded</li>
                    <li>• Detailed feedback</li>
                    <li>• Qualification status</li>
                  </ul>
                </div>
              </div>

              <Separator className="my-4" />

              <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <HelpCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-700 dark:text-amber-400">Scoring Criteria</h4>
                  <p className="text-sm text-amber-600 dark:text-amber-300 mt-1">
                    You&apos;ll be evaluated on safe questioning, boundary awareness, neutral language, and proper redirection techniques. 
                    Score 80+ to be qualified, 60-79 needs improvement, below 60 requires retraining.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Results History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5" />
                My Assessment History
              </CardTitle>
              <CardDescription>
                View your past assessments and track your progress
              </CardDescription>
            </CardHeader>
            <CardContent>
              {myTests.length === 0 ? (
                <div className="text-center py-8">
                  <Award className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="font-medium text-lg">No Assessments Yet</h3>
                  <p className="text-muted-foreground mt-1">
                    Take your first assessment to see your results here
                  </p>
                  <Button className="mt-4" onClick={handleStartAssessment}>
                    Start Assessment
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {myTests.map((test) => (
                    <div 
                      key={test.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={cn(
                          "p-2 rounded-lg",
                          test.test_status === 'completed' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-800'
                        )}>
                          {test.test_status === 'completed' ? (
                            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                          ) : (
                            <Clock className="h-5 w-5 text-gray-500" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              {test.is_mock ? 'Mock Test' : 'Assessment'}
                            </span>
                            <StatusBadge status={test.test_status} />
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {new Date(test.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {test.score != null && (
                          <div className="text-right">
                            <div className="text-2xl font-bold">{test.score}%</div>
                            <StatusBadge status={test.qualification_status} />
                          </div>
                        )}
                        {test.test_status === 'completed' && (
                          <Button variant="outline" size="sm" onClick={() => handleViewResults(test)}>
                            View Details
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Library Tab */}
        <TabsContent value="library" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                EEOC Reference Library
              </CardTitle>
              <CardDescription>
                Essential guidelines and best practices for compliant interviewing
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {LIBRARY_SECTIONS.map((section) => {
                  const Icon = section.icon;
                  return (
                    <Card key={section.id} className="border">
                      <CardHeader className="pb-2">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                            <Icon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                          </div>
                          <div>
                            <CardTitle className="text-base">{section.title}</CardTitle>
                            <CardDescription className="text-xs">{section.description}</CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2">
                          {section.content.map((item, idx) => (
                            <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                              <ChevronRight className="h-4 w-4 text-purple-500 mt-0.5 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
